import { YomiriMobileRemote } from "./components/YomiriMobileRemote.tsx";
import { CastCoordinator } from '../cast/CastCoordinator.tsx';
import { getRemoteSnapshot } from '../cast/RemoteSession.ts';
import { YomiriTvReceiverOverlay } from "./components/YomiriTvReceiverOverlay.tsx";
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, lazy as reactLazy, Suspense } from "react";
import { YomiriUpdater } from './components/YomiriUpdater.tsx';
import { RetainedView } from './components/RetainedView.tsx';
import { YomiriLoading } from './components/YomiriLoading.tsx';
import { ThemeProvider } from "@mui/material/styles";
import './desktop.css';
import './yomiri-tokens.css';
import './views/media-hub.css';
import { useTvNavigation, isTvMode } from './components/useTvNavigation.ts';
import { yomiriTheme } from "./theme.ts";
import { createYomiriAppearance } from "./mobileTheme.ts";
import { readAppearance } from "./appearance.ts";
const lazy: typeof reactLazy = factory => reactLazy(factory);
const YomiriAppearanceView = lazy(() => import("./views/YomiriAppearanceView.tsx").then(module => ({ default: module.YomiriAppearanceView })));
import "./motion.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Snackbar from '@mui/material/Snackbar';
import {startDiscoveryAlerts} from '../metadata/DiscoveryAlerts.ts';

import { YomiriHeader } from "./components/YomiriHeader.tsx";
import { YomiriSidebar } from "./components/YomiriSidebar.tsx";
const YomiriFooter = lazy(() => import("./components/YomiriFooter.tsx").then(module => ({ default: module.YomiriFooter })));
import { YomiriMobileTopBar } from "@/yomiri/ui/components/YomiriMobileTopBar.tsx";
import { YomiriMobileBottomNav } from "@/yomiri/ui/components/YomiriMobileBottomNav.tsx";
const YomiriUpdatesView = lazy(() => import("./views/YomiriUpdatesView.tsx").then(module => ({ default: module.YomiriUpdatesView })));
const YomiriHistoryView = lazy(() => import("./views/YomiriHistoryView.tsx").then(module => ({ default: module.YomiriHistoryView })));
const YomiriSettingsView = lazy(() => import('./views/YomiriSettingsView.tsx').then(module => ({ default: module.YomiriSettingsView })));
const YomiriMobileExtensionsView = lazy(() => import("./views/YomiriMobileExtensionsView.tsx").then(module => ({ default: module.YomiriMobileExtensionsView })));

import { YomiriHomeView } from "./views/YomiriHomeView.tsx";
import { NyomiriSkeletonDetail, NyomiriSkeletonGrid } from './components/skeletons/NyomiriSkeletons.tsx';
import { YomiriDetailView } from './views/YomiriDetailView.tsx';
const YomiriDiscoveryDetailView = YomiriDetailView;
const YomiriCatalogView = lazy(() => import('./views/YomiriCatalogView.tsx').then(m => ({ default: m.YomiriCatalogView })));
import { YomiriMangaEntry as YomiriExploreView } from "./views/YomiriMangaEntry.tsx";
const YomiriAboutView = lazy(() => import("./views/YomiriAboutView.tsx").then(module => ({ default: module.YomiriAboutView })));
const YomiriMangaReaderView = lazy(() => import('./views/YomiriMangaReaderView.tsx').then(m => ({ default: m.YomiriMangaReaderView })));
const YomiriLibraryView = lazy(() => import('./views/YomiriLibraryView.tsx').then(m => ({ default: m.YomiriLibraryView })));
const YomiriSourcesView = lazy(() => import('./views/YomiriSourcesView.tsx').then(m => ({default:m.YomiriSourcesView})));
const YomiriLiveView = lazy(() => import('./views/YomiriLiveView.tsx').then(m => ({default:m.YomiriLiveView})));
import { YomiriAnimeEntry as YomiriAnimeView } from './views/YomiriAnimeEntry.tsx';
import { resolveNavigationUrl } from '../utils/navigation.ts';
const DynamicSectionScreen = lazy(() => import("../plugins/ui/DynamicSectionScreen.tsx").then(m => ({ default: m.DynamicSectionScreen })));

import type { YomiriManga, YomiriChapter } from "@/yomiri/types/extension.ts";

export function YomiriApp() {
  useTvNavigation();
  useEffect(() => {
    // Al montarse React y preparar el primer render, desvanecemos el splash de arranque
    if (typeof (window as any).dismissYomiriSplash === 'function') {
      (window as any).dismissYomiriSplash();
    }
  }, []);
  const mobile = useMediaQuery(yomiriTheme.breakpoints.down("md"));
  const receiver = !mobile || isTvMode() || new URLSearchParams(location.search).has('receiver');
  const [appearance, setAppearance] = useState(readAppearance);
  useEffect(() => { const update = () => setAppearance(readAppearance()); window.addEventListener('yomiri-appearance-changed', update); return () => window.removeEventListener('yomiri-appearance-changed', update); }, []);
  const activeTheme = useMemo(() => createYomiriAppearance(appearance, mobile), [appearance, mobile]);
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = activeTheme.palette.background.default;
  }, [activeTheme]);
  const [storageRequested, setStorageRequested] = useState(false);
  const [updatesRevision, setUpdatesRevision] = useState(0);
  const libraryAction = (action: string) => window.dispatchEvent(new Event("yomiri-library-" + action));
  const mainRef = useRef<HTMLElement | null>(null);
  const scrollPositions = useRef(new Map<string, number>());
  const savedRoutes = useRef(new Map<string, string>());
  useEffect(() => {
    let disposed = false; let loaded = false; let release: (() => void) | undefined;
    const checkPending = () => {
      if (loaded || !sessionStorage.getItem('yomiri_pending_verification')) return;
      loaded = true;
      void import('../core/SourceVerification.ts').then(module => { if (!disposed) release = module.watchSourceVerification(); });
    };
    checkPending(); window.addEventListener('focus', checkPending); document.addEventListener('visibilitychange', checkPending);
    return () => { disposed = true; release?.(); window.removeEventListener('focus', checkPending); document.removeEventListener('visibilitychange', checkPending); };
  }, []);
  const [verificationNotice,setVerificationNotice]=useState('');
  useEffect(()=>{
    const listener=(event:Event)=>setVerificationNotice(String((event as CustomEvent<string>).detail||''));
    window.addEventListener('yomiri-discovery-notice',listener);
    const stop=startDiscoveryAlerts(setVerificationNotice);
    return()=>{stop();window.removeEventListener('yomiri-discovery-notice',listener);};
  },[]);
  useEffect(()=>{
    const listener=(event:Event)=>setVerificationNotice((event as CustomEvent<{message:string}>).detail.message);
    window.addEventListener('yomiri-source-verification',listener);
    return()=>window.removeEventListener('yomiri-source-verification',listener);
  },[]);
  const [currentView, setCurrentView] = useState<string>(
    () => {
      const resolved = resolveNavigationUrl(location.search);
      return resolved.section || (typeof window !== "undefined" && window.innerWidth < 900 ? "library" : "home");
    },
  );
  useEffect(() => {
    if(document.documentElement.dataset.yomiriTv !== 'true' || currentView === 'reader') return;
    const main=mainRef.current;if(!main)return;
    const focusContent=()=>{const target=Array.from(main.querySelectorAll<HTMLElement>('button,a[href],input,select,video[controls]')).find(el=>!el.closest('footer,[inert],[aria-hidden=true],[data-y-view=hidden]')&&!el.matches(':disabled')&&el.getClientRects().length>0);if(target){target.focus({preventScroll:true});return document.activeElement===target;}return false;};
    if(focusContent())return;
    const observer=new MutationObserver(()=>{if(focusContent())observer.disconnect();});observer.observe(main,{subtree:true,childList:true,attributes:true,attributeFilter:['style','hidden']});return()=>observer.disconnect();
  },[currentView]);
  const [exploreSubTab, setExploreSubTab] = useState<"sources" | "extensions" | "migrate">("sources");
  const [selectedSource, setSelectedSource] = useState<string | null>(() => new URLSearchParams(location.search).get("source"));
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [libraryFiltersOpen,setLibraryFiltersOpen]=useState(false);
  const [previousView, setPreviousView] = useState<string>(window.innerWidth < 900 ? "library" : "home");
  const activeViewRef = useRef(currentView);
  activeViewRef.current = currentView;
  useLayoutEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = scrollPositions.current.get(currentView) ?? 0;
  }, [currentView]);
  const [selectedMangaId, setSelectedMangaId] = useState<string>(
    () => new URLSearchParams(location.search).get("manga") || "",
  );
  const [selectedChapterId, setSelectedChapterId] = useState<string | undefined>(
    () => new URLSearchParams(location.search).get("chapter") || undefined,
  );
  const [selectedManga, setSelectedManga] = useState<YomiriManga | undefined>(() => {
    try {
      return (
        JSON.parse(
          sessionStorage.getItem(
            `yomiri-route:${new URLSearchParams(location.search).get("manga")}`,
          ) || "null",
        ) || undefined
      );
    } catch {
      return undefined;
    }
  });
  const [resumeChapter, setResumeChapter] = useState<YomiriChapter | undefined>();
  const [repositoriesReady, setRepositoriesReady] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("yomiri_repositories") || "[]").length > 0;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const refresh = () => {
      try {
        setRepositoriesReady(
          !!getRemoteSnapshot()?.repositories.length || JSON.parse(localStorage.getItem("yomiri_repositories") || "[]").length > 0,
        );
      } catch {
        setRepositoriesReady(false);
      }
    };
    window.addEventListener("yomiri-sources-changed", refresh);
    return () => window.removeEventListener("yomiri-sources-changed", refresh);
  }, []);
  useEffect(() => {
    const restore = () => {
      scrollPositions.current.set(activeViewRef.current, mainRef.current?.scrollTop ?? 0);
      const resolved = resolveNavigationUrl(location.search);
      setCurrentView(resolved.section || (window.innerWidth < 900 ? "library" : "home"));
      setSelectedSource(resolved.sourceId || null);
      setSelectedMangaId(resolved.mangaId || "");
      setSelectedChapterId(resolved.chapterId || undefined);
      setResumeChapter(undefined);
      try {
        setSelectedManga(
          JSON.parse(sessionStorage.getItem(`yomiri-route:${resolved.mangaId}`) || "null") ||
            undefined,
        );
      } catch {
        setSelectedManga(undefined);
      }
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, []);
  const writeRoute = (view: string, mangaId?: string, chapterId?: string) => {
    savedRoutes.current.set(currentView, location.pathname + location.search);
    if (!mangaId && view !== 'reader' && savedRoutes.current.has(view)) {
      history.pushState({}, '', savedRoutes.current.get(view)!);
      return;
    }
    const params = new URLSearchParams({ section: view });
    if (mangaId) params.set("manga", mangaId);
    if (chapterId) params.set("chapter", chapterId);
    history.pushState({}, "", `/?${params}`);
  };

  const handleNavigate = (view: string, params?: any) => {
    scrollPositions.current.set(currentView, mainRef.current?.scrollTop ?? 0);
    if (params?.manga) {
      handleOpenManga(params.manga.id, params.chapterId, params.manga);
      return;
    }

    if (view === "reader" && params?.mangaId) {
      handleOpenManga(params.mangaId, params.chapterId, params.manga);
      return;
    }

    if (params?.filter) {
      setCurrentView("library");
      return;
    }

    writeRoute(view);
    setSelectedSource(new URLSearchParams(location.search).get('source'));
    setCurrentView(view);
  };

  const handleOpenManga = (
    mangaId: string,
    chapterId?: string,
    mangaObj?: YomiriManga,
    chapter?: YomiriChapter,
  ) => {
    scrollPositions.current.set(currentView, mainRef.current?.scrollTop ?? 0);
    if (currentView !== "reader") setPreviousView(currentView);
    setSelectedMangaId(mangaId);
    setSelectedChapterId(chapterId);
    setSelectedManga(mangaObj);
    setResumeChapter(chapter);
    if (mangaObj) sessionStorage.setItem(`yomiri-route:${mangaId}`, JSON.stringify(mangaObj));
    if (!chapterId && mangaObj?.sourceId) {
      history.pushState({}, '', '/?' + new URLSearchParams({section:'detail',manga:mangaId,source:mangaObj.sourceId}));
      setCurrentView('detail');
      return;
    }
    writeRoute("reader", mangaId, chapterId);
    setCurrentView("reader");
  };

  return (
    <ThemeProvider theme={activeTheme}>
      <CssBaseline />
      <YomiriUpdater section={currentView} />
      <Snackbar open={!!verificationNotice} message={verificationNotice} autoHideDuration={8000} onClose={()=>setVerificationNotice('')} />
      <Box
        className="yomiri-desktop"
        sx={{
          width: "100%",
          height: "100dvh",
          pb: { xs: (currentView === "reader" || !!selectedSource && ["sources", "explore"].includes(currentView)) ? 0 : "calc(56px + env(safe-area-inset-bottom))", md: 0 },
          backgroundColor: "var(--y-surface)",
          color: "var(--y-onSurface)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Mobile Top App Bar (<= 768px) */}
        {!["reader", "anime", "sources", "live", "explore"].includes(currentView) && (
          <YomiriMobileTopBar
            currentView={currentView}
            onNavigate={handleNavigate}
            onOpenFilter={() => setLibraryFiltersOpen(true)}
            searchQuery={mobileSearchQuery}
            onSearchChange={setMobileSearchQuery}
            exploreTab={exploreSubTab}
            onExploreTabChange={tab => { setExploreSubTab(tab); setSelectedSource(null); history.replaceState({}, "", "/?section=explore"); }}
            onOpenStorageConfig={() => libraryAction("storage")}
            onExportBackup={() => libraryAction("export")}
            onImportBackup={() => libraryAction("import")}
            onRefreshUpdates={() => setUpdatesRevision(value => value + 1)}
          />
        )}

        {/* Top Navigation Header (> 768px) */}
        {!mobile && currentView !== "reader" && <YomiriHeader onNavigate={handleNavigate} onOpenManga={handleOpenManga} />}

        {/* Main Middle Row (Sidebar + Body + Right Panel) */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            width: "100%",
            position: "relative",
          }}
        >
          {/* Left Sidebar (Desktop only) */}
          {!mobile && <YomiriSidebar currentView={currentView} onNavigate={handleNavigate} />}

          {/* Central Scrollable Content */}
          <Box
            component="main"
            ref={mainRef}
            onScroll={event => scrollPositions.current.set(currentView, event.currentTarget.scrollTop)}
            sx={{
              flex: 1,
              minWidth: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "var(--y-surface)",
            }}
          >
            {!repositoriesReady && currentView !== 'home' && currentView !== 'detail' &&
              !["sources", "library", "downloads", "reader", "appearance", "about", "anime", "updates", "history", "more", "live", "plugins"].includes(currentView) && !currentView.startsWith('section:') && (
                <Box
                  sx={{
                    maxWidth: 620,
                    mx: "auto",
                    width: "100%",
                    p: { xs: 3, md: 7 },
                    textAlign: "center",
                  }}
                >
                  <Typography component="h1" sx={{ fontSize: 25, fontWeight: 600, mb: 1.5 }}>
                    Agregá un catálogo para empezar
                  </Typography>
                  <Typography
                    sx={{
                      color: "var(--y-onSurfaceVariant)",
                      fontSize: 14,
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    Agregá un repositorio desde Fuentes. Tus capítulos descargados siguen
                    disponibles en Biblioteca.
                  </Typography>
                  <Button variant="contained" onClick={() => handleNavigate("sources")}>
                    Ir a Fuentes
                  </Button>
                </Box>
              )}
            {currentView === "about" && <Suspense fallback={<YomiriLoading label="Cargando información" />}><YomiriAboutView /></Suspense>}
            {currentView === "appearance" && <Suspense fallback={<YomiriLoading label="Cargando apariencia" />}><YomiriAppearanceView /></Suspense>}
            {currentView === 'detail' && <Suspense fallback={<NyomiriSkeletonDetail />}><YomiriDetailView onNavigate={handleNavigate} onOpenManga={handleOpenManga}/></Suspense>}
            <RetainedView active={currentView === 'live'}><YomiriLiveView onNavigate={handleNavigate} /></RetainedView>
            <RetainedView active={currentView === 'sources' || currentView === 'plugins'}><YomiriSourcesView onNavigate={handleNavigate} /></RetainedView>
            {currentView.startsWith('section:') && (
              <Suspense fallback={<YomiriLoading label="Cargando sección" />}>
                <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto', width: '100%' }}>
                  <DynamicSectionScreen sectionId={currentView.replace('section:', '')} />
                </Box>
              </Suspense>
            )}
            <RetainedView active={currentView === 'anime'}><YomiriAnimeView /></RetainedView>
            <RetainedView active={currentView === "home"}>
              <YomiriHomeView onOpenManga={handleOpenManga} onNavigate={handleNavigate} />
            </RetainedView>

            {/* Updates View */}
            <RetainedView active={currentView === "updates"}>
              <YomiriUpdatesView onOpenManga={handleOpenManga} revision={updatesRevision} />
            </RetainedView>

            {/* History View */}
            <RetainedView active={currentView === "history"}>
              <YomiriHistoryView onOpenManga={handleOpenManga} searchQuery={mobileSearchQuery} />
            </RetainedView>

            {/* More View */}
            <RetainedView active={currentView === "more"}>
              <YomiriSettingsView onNavigate={handleNavigate} onOpenStorage={() => { setStorageRequested(true); handleNavigate('library'); }} />
            </RetainedView>

            {currentView === "reader" && (
              <Suspense fallback={<YomiriLoading label="Abriendo lector" />}>
              {!selectedChapterId ? <YomiriDiscoveryDetailView initialManga={selectedManga} onNavigate={handleNavigate} onOpenManga={handleOpenManga}/> : <YomiriMangaReaderView
                mangaId={selectedMangaId}
                initialChapterId={selectedChapterId}
                initialManga={selectedManga}
                resumeChapter={resumeChapter}
                onBack={() => handleNavigate(previousView)}
              />}
              </Suspense>
            )}

            <RetainedView active={currentView === "library" || currentView === "downloads"}>
              <YomiriLibraryView
                onOpenManga={handleOpenManga}
                onNavigate={handleNavigate}
                mobileSearchQuery={mobileSearchQuery}
                mobileFilterSheetOpen={libraryFiltersOpen}
                onCloseMobileFilterSheet={()=>setLibraryFiltersOpen(false)}
                requestedSection={currentView === "downloads" ? "downloads" : "manga"}
                openStorageRequested={storageRequested}
                onStorageOpened={() => setStorageRequested(false)}
              />
            </RetainedView>

            <RetainedView active={currentView === "explore"}>
              <YomiriExploreView key={selectedSource || "manga-catalog"} onOpenManga={handleOpenManga} onNavigate={handleNavigate} />
            </RetainedView>

            {currentView === "catalog" && (
              <Suspense fallback={<NyomiriSkeletonGrid count={12} />}>
                <YomiriCatalogView onOpenManga={handleOpenManga} onNavigate={handleNavigate} />
              </Suspense>
            )}

          </Box>
        </Box>

        {/* Mobile Bottom Navigation Bar (<= 768px) */}
        {currentView !== "reader" && (
          <YomiriMobileBottomNav currentView={currentView} onNavigate={handleNavigate} />
        )}
      </Box>
        <CastCoordinator route={location.search} remote={!receiver} />
        {receiver && <YomiriTvReceiverOverlay onNavigate={handleNavigate} onOpenManga={handleOpenManga} onSearch={setMobileSearchQuery} />}
        {!receiver && <YomiriMobileRemote />}
      </ThemeProvider>
  );
}
